import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Mail, Star, Briefcase, DollarSign, Clock, CheckCircle } from 'lucide-react';

const EMAIL_TEMPLATES = {
  professional: {
    id: 'professional',
    name: 'Professional Introduction',
    icon: Briefcase,
    category: 'General',
    subject: 'Janitorial Services Proposal - {{company_name}}',
    message: `Dear {{contact_name}},

Thank you for the opportunity to present our janitorial services proposal for {{company_name}}.

We have carefully reviewed your facility requirements and prepared a comprehensive cleaning plan tailored to your specific needs. Our proposal includes:

• Complete cleaning specifications for all areas
• Transparent pricing with no hidden fees
• Flexible service schedules
• Quality assurance guarantees

Please find the detailed proposal attached. We are confident that our services will exceed your expectations and provide exceptional value for your facility.

I would welcome the opportunity to discuss this proposal with you at your convenience.

Best regards`,
    tags: ['Professional', 'Formal', 'General']
  },
  
  competitive: {
    id: 'competitive',
    name: 'Competitive Pricing Focus',
    icon: DollarSign,
    category: 'Sales',
    subject: 'Competitive Janitorial Proposal - Save {{savings_percent}}%',
    message: `Hi {{contact_name}},

Great news! We've prepared a highly competitive proposal for {{company_name}}'s janitorial needs.

🎯 **Why Choose Us:**
• **Competitive Pricing**: {{monthly_price}}/month - potentially saving you money
• **Premium Service**: Hospital-grade cleaning standards
• **Flexible Terms**: No long-term contracts required
• **Local Team**: Fast response times, personalized service

Our attached proposal outlines everything in detail. We're confident we can deliver exceptional value while maintaining the highest quality standards.

Let's schedule a quick call to discuss how we can serve your facility better.

Looking forward to working with you!`,
    tags: ['Sales', 'Competitive', 'Value']
  },
  
  premium: {
    id: 'premium',
    name: 'Premium Service Package',
    icon: Star,
    category: 'Premium',
    subject: 'Premium Janitorial Services - {{company_name}}',
    message: `Dear {{contact_name}},

We are pleased to present our premium janitorial services proposal for {{company_name}}.

As a leader in commercial cleaning, we specialize in providing white-glove service to discerning clients who demand excellence. Our proposal includes:

✨ **Premium Features:**
• Certified cleaning technicians with advanced training
• Eco-friendly, hospital-grade cleaning products
• Real-time quality monitoring and reporting
• Dedicated account manager
• 24/7 emergency response

Your facility deserves the best, and we are committed to delivering exceptional results every single day.

The attached proposal provides comprehensive details about our services, pricing, and quality guarantees.

I look forward to partnering with you.

Sincerely`,
    tags: ['Premium', 'High-End', 'Executive']
  },
  
  follow_up: {
    id: 'follow_up',
    name: 'Follow-Up Reminder',
    icon: Clock,
    category: 'Follow-Up',
    subject: 'Following Up: Proposal for {{company_name}}',
    message: `Hi {{contact_name}},

I wanted to follow up on the janitorial services proposal I sent you on {{sent_date}}.

I understand you're busy, so I wanted to make this easy:

📄 **Quick Recap:**
• Monthly Service: {{monthly_price}}
• Frequency: {{frequency}}
• Service Areas: {{area_count}} zones
• Start Date: Flexible to your schedule

**Next Steps:**
1. Review the attached proposal
2. Let me know if you have any questions
3. We can adjust anything to better fit your needs

Would you be available for a brief call this week to discuss?

Thanks for your consideration!`,
    tags: ['Follow-Up', 'Reminder', 'Gentle']
  },
  
  urgent: {
    id: 'urgent',
    name: 'Time-Sensitive Offer',
    icon: Clock,
    category: 'Sales',
    subject: '⏰ Limited Time Offer - {{company_name}} Proposal',
    message: `Hi {{contact_name}},

I have great news! We can offer {{company_name}} a special rate if we can finalize by {{deadline_date}}.

**🎁 Limited-Time Benefits:**
• First month at 20% discount
• Free deep-cleaning for first service
• Waived setup fees
• Lock in current pricing for 12 months

This proposal is attached and includes our complete service package at {{monthly_price}}/month.

**This offer expires {{deadline_date}}**, so I wanted to make sure you didn't miss this opportunity.

Can we schedule a quick call to finalize the details?

Best regards`,
    tags: ['Urgent', 'Limited-Time', 'Sales']
  },
  
  referral: {
    id: 'referral',
    name: 'Referral Introduction',
    icon: Star,
    category: 'Referral',
    subject: '{{referrer_name}} Recommended Us - Proposal for {{company_name}}',
    message: `Hi {{contact_name}},

{{referrer_name}} at {{referrer_company}} recommended I reach out to you regarding janitorial services for {{company_name}}.

We've been proud to serve {{referrer_company}} for {{referrer_duration}}, and they suggested we'd be a great fit for your facility as well.

**What We've Done for {{referrer_company}}:**
• Consistent 5-star service ratings
• Zero missed services in {{referrer_duration}}
• Flexible scheduling that works with their operations

I've prepared a customized proposal based on your facility's needs (attached). 

Would you be open to a brief introduction call? I'd love to learn more about your current challenges and how we can help.

Thank you for the opportunity!`,
    tags: ['Referral', 'Warm-Lead', 'Trust']
  },
  
  won_client: {
    id: 'won_client',
    name: 'Welcome New Client',
    icon: CheckCircle,
    category: 'Onboarding',
    subject: 'Welcome to {{company_name}}! Your Service Agreement',
    message: `Dear {{contact_name}},

Welcome! We're thrilled to begin serving {{company_name}}.

🎉 **What Happens Next:**

**This Week:**
• Final walkthrough scheduled for {{walkthrough_date}}
• Team assignments and training
• Supply procurement and staging

**Your First Service: {{start_date}}**
• Our team will arrive at {{start_time}}
• Introduction to your dedicated supervisor
• Quality check and feedback session

**Your Service Agreement:**
• Monthly Rate: {{monthly_price}}
• Service Days: {{service_days}}
• Dedicated Support: {{manager_phone}}

Please review the attached service agreement. If you have any questions before we start, I'm here to help.

Thank you for choosing us. We're committed to exceeding your expectations!

Warmly`,
    tags: ['Welcome', 'Onboarding', 'Agreement']
  }
};

export function EmailTemplateLibrary({ onSelectTemplate, proposal }) {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [previewTemplate, setPreviewTemplate] = useState(null);

  const categories = ['all', 'General', 'Sales', 'Premium', 'Follow-Up', 'Referral', 'Onboarding'];

  const filterTemplates = () => {
    if (selectedCategory === 'all') return Object.values(EMAIL_TEMPLATES);
    return Object.values(EMAIL_TEMPLATES).filter(t => t.category === selectedCategory);
  };

  const replaceVariables = (text, proposal) => {
    if (!proposal) return text;
    
    const variables = {
      '{{company_name}}': proposal.company_name || '[Company Name]',
      '{{contact_name}}': proposal.contact_name || '[Contact Name]',
      '{{monthly_price}}': proposal.monthly_price ? `$${proposal.monthly_price.toFixed(2)}` : '[Price]',
      '{{frequency}}': (proposal.frequency || 'weekly').replace('_', ' '),
      '{{sent_date}}': new Date().toLocaleDateString(),
      '{{deadline_date}}': new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString(),
      '{{area_count}}': '4',
      '{{start_date}}': new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toLocaleDateString(),
      '{{start_time}}': '6:00 PM',
      '{{service_days}}': 'Monday, Wednesday, Friday',
      '{{manager_phone}}': '(555) 123-4567',
      '{{walkthrough_date}}': new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toLocaleDateString(),
      '{{referrer_name}}': '[Referrer Name]',
      '{{referrer_company}}': '[Referrer Company]',
      '{{referrer_duration}}': '2 years',
      '{{savings_percent}}': '15'
    };

    let result = text;
    Object.entries(variables).forEach(([key, value]) => {
      result = result.replace(new RegExp(key, 'g'), value);
    });
    
    return result;
  };

  const handleSelectTemplate = (template) => {
    const subject = replaceVariables(template.subject, proposal);
    const message = replaceVariables(template.message, proposal);
    
    onSelectTemplate({
      subject,
      message,
      template_id: template.id
    });
  };

  const TemplateCard = ({ template }) => {
    const Icon = template.icon;
    
    return (
      <Card className="hover:shadow-md transition-shadow cursor-pointer">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-teal-50 rounded-lg">
                <Icon className="h-5 w-5 text-teal-600" />
              </div>
              <div>
                <CardTitle className="text-base">{template.name}</CardTitle>
                <CardDescription className="text-xs">{template.category}</CardDescription>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="text-xs text-muted-foreground">
            <strong>Subject:</strong> {replaceVariables(template.subject, proposal).substring(0, 50)}...
          </div>
          <div className="text-xs text-muted-foreground line-clamp-3">
            {replaceVariables(template.message, proposal).substring(0, 150)}...
          </div>
          <div className="flex flex-wrap gap-1">
            {template.tags.map(tag => (
              <Badge key={tag} variant="secondary" className="text-xs">
                {tag}
              </Badge>
            ))}
          </div>
          <div className="flex gap-2 pt-2">
            <Button 
              size="sm" 
              onClick={() => setPreviewTemplate(template)}
              variant="outline"
              className="flex-1"
            >
              Preview
            </Button>
            <Button 
              size="sm" 
              onClick={() => handleSelectTemplate(template)}
              className="flex-1"
            >
              Use Template
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (previewTemplate) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Preview: {previewTemplate.name}</h3>
          <Button variant="outline" onClick={() => setPreviewTemplate(null)}>
            Back to Templates
          </Button>
        </div>
        
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Subject</CardTitle>
            <div className="text-base font-normal">
              {replaceVariables(previewTemplate.subject, proposal)}
            </div>
          </CardHeader>
          <CardContent>
            <CardTitle className="text-sm mb-3">Message</CardTitle>
            <div className="whitespace-pre-wrap text-sm bg-gray-50 p-4 rounded-lg">
              {replaceVariables(previewTemplate.message, proposal)}
            </div>
          </CardContent>
        </Card>

        <div className="flex gap-2">
          <Button onClick={() => handleSelectTemplate(previewTemplate)}>
            Use This Template
          </Button>
          <Button variant="outline" onClick={() => setPreviewTemplate(null)}>
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Email Templates
          </h3>
          <p className="text-sm text-muted-foreground">Choose a professional template to customize</p>
        </div>
      </div>

      <Tabs value={selectedCategory} onValueChange={setSelectedCategory}>
        <TabsList className="w-full justify-start overflow-x-auto">
          {categories.map(cat => (
            <TabsTrigger key={cat} value={cat} className="capitalize">
              {cat}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filterTemplates().map(template => (
          <TemplateCard key={template.id} template={template} />
        ))}
      </div>
    </div>
  );
}
